import React from 'react';
import { StatCardProps } from '../types';
import { DollarSign, Package, TrendingUp, AlertTriangle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, trend, color }) => (
  <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between mb-4">
      <div className={`p-3 rounded-full ${color} bg-opacity-10 text-opacity-100`}>
        {/* FIX: Cast icon to React.ReactElement<any> to resolve typing issue with className prop. */}
        {React.cloneElement(icon as React.ReactElement<any>, { className: color.replace('bg-', 'text-') })}
      </div>
      {trend && <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">{trend}</span>}
    </div>
    <h3 className="text-slate-500 text-sm font-medium mb-1">{title}</h3>
    <p className={`text-2xl font-bold ${title.toLowerCase().includes('sales') || title.toLowerCase().includes('profit') ? 'text-emerald-600' : title.toLowerCase().includes('expenditure') || title.toLowerCase().includes('low stock') ? 'text-red-600' : 'text-slate-900'}`}>{value}</p>
  </div>
);

const data = [
  { name: 'Mon', sales: 4000 },
  { name: 'Tue', sales: 3000 },
  { name: 'Wed', sales: 2000 },
  { name: 'Thu', sales: 2780 },
  { name: 'Fri', sales: 1890 },
  { name: 'Sat', sales: 2390 },
  { name: 'Sun', sales: 3490 },
];

const Dashboard: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Sales (Today)" 
          value="RS. 1,245.00" 
          icon={<DollarSign size={24} />} 
          trend="+12%" 
          color="bg-emerald-500" 
        />
        <StatCard 
          title="Total Orders" 
          value="45" 
          icon={<Package size={24} />} 
          color="bg-blue-500" 
        />
        <StatCard 
          title="Net Profit (Month)" 
          value="RS. 8,540.00" 
          icon={<TrendingUp size={24} />} 
          trend="+5.2%" 
          color="bg-emerald-500" 
        />
        <StatCard 
          title="Low Stock Items" 
          value="12" 
          icon={<AlertTriangle size={24} />} 
          color="bg-red-500" 
        />
      </div>

      {/* Main Chart Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h3 className="text-lg font-bold text-slate-800 mb-6">Weekly Sales Overview</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748b'}} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b'}} />
                <Tooltip />
                <Area type="monotone" dataKey="sales" stroke="#10b981" fillOpacity={1} fill="url(#colorSales)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Activity / Notifications */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h3 className="text-lg font-bold text-slate-800 mb-6">Recent Alerts</h3>
          <div className="space-y-4">
            {[
              { title: "Panadol Extra low on stock", time: "2 hours ago", type: "warning" },
              { title: "New shipment from Ali Distributors", time: "5 hours ago", type: "info" },
              { title: "Invoice #INV-2023-001 paid", time: "Yesterday", type: "success" },
              { title: "Expiry warning: Insulin Batch A23", time: "2 days ago", type: "danger" }
            ].map((alert, i) => (
              <div key={i} className="flex gap-4 items-start p-3 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer border-b border-slate-50 last:border-0">
                 <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${
                   alert.type === 'warning' ? 'bg-orange-500' :
                   alert.type === 'info' ? 'bg-blue-500' :
                   alert.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'
                 }`} />
                 <div>
                   <p className="text-sm font-medium text-slate-800">{alert.title}</p>
                   <p className="text-xs text-slate-500">{alert.time}</p>
                 </div>
              </div>
            ))}
          </div>
          <button className="w-full mt-6 py-2 text-sm text-emerald-600 font-medium hover:bg-emerald-50 rounded-lg transition-colors">
            View All Notifications
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;